'use es6';

import I18n from 'I18n';
import PropTypes from 'prop-types';
import { Component } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { CUSTOM_UNIT_RANGE_TYPES, DEFAULT_RANGE_TYPES, FISCAL_YEAR_ENABLED_DEFAULT_RANGE_TYPES, RANGE_TYPES, RELATIVE_RANGE_TYPES, ROLLING_DATE_TYPES } from '../constants/DateRangeConstants';
import { MicroDateRange } from 'foundations-components/transitional/components/MicroDateRange';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import { Select } from 'foundations-components/transitional/components/Select';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { isValid, fromMoment, toMoment, SimpleDate, compare } from 'UIComponents/core/SimpleDate';
import makeDateRangeByType from 'reporting-data/lib/makeDateRangeByType';
import withPartials from 'reporting-ui-components/lib/withPartials';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import FullDatePeriodInput from './FullDatePeriodInput';
import { FISCAL_YEAR_TO_STANDARD_DATE_RANGE, STANDARD_TO_FISCAL_YEAR_DATE_RANGE } from 'reporting-data/constants/dateRangeTypes';
import { FiscalYearContext } from '../../PlatformReport/FiscalYearProvider';
import { isFiscalYearRangeType } from 'reporting-data/lib/fiscalYearUtils';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DATE_FORMAT = 'YYYY-MM-DD';
function getFormattedRangeTypeOptions(options) {
  return options.map(rangeTypeKey => ({
    rangeTypeKey,
    text: `DateRangePicker.RANGE_TYPES.text.${rangeTypeKey}`,
    help: `DateRangePicker.RANGE_TYPES.help.${rangeTypeKey}`
  }));
}
class DateRangePicker extends Component {
  constructor(props) {
    super(props);
    this.formatDate = date => {
      const {
        format
      } = this.props;
      if (date && date.format) {
        return date.format(format);
      }
      return I18n.moment.portalTz().format(format);
    };
    this.getRangeTypeOptions = () => {
      const {
        rangeTypeOptions,
        rangeTypeOverrides,
        isFiscalYearEnabled,
        showRelativeRangeTypes
      } = this.props;
      const rangeTypes = Object.values(isFiscalYearEnabled ? FISCAL_YEAR_ENABLED_DEFAULT_RANGE_TYPES : DEFAULT_RANGE_TYPES);
      if (showRelativeRangeTypes) {
        rangeTypes.push(...RELATIVE_RANGE_TYPES);
      }
      if (rangeTypeOverrides) {
        return rangeTypeOverrides;
      }
      if (!rangeTypeOptions) {
        return getFormattedRangeTypeOptions(rangeTypes);
      }
      const restrictedRangeTypeOptions = rangeTypes.concat(CUSTOM_UNIT_RANGE_TYPES).filter(key => rangeTypeOptions.includes(key));
      return getFormattedRangeTypeOptions(restrictedRangeTypeOptions);
    };
    this.getDropdownOptions = (rangeTypeOptions, callback) => rangeTypeOptions.map(option => /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: option.help
        }),
        placement: option.placement,
        children: /*#__PURE__*/_jsxs(Button, {
          onClick: this.props.partial(callback, option.rangeTypeKey),
          children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
            message: option.text
          }), option.isLocked && /*#__PURE__*/_jsx(LockBadge, {})]
        })
      })
    }, option.rangeTypeKey));
    this.getRollingDateRangeSelectOptions = () => {
      const {
        rollingDateOptions,
        rollingDateOverrides
      } = this.props;
      if (rollingDateOverrides) return rollingDateOverrides;
      const options = rollingDateOptions || Object.values(ROLLING_DATE_TYPES);
      return options.map(value => ({
        help: I18n.text(`DateRangePicker.ROLLING_DATE_TYPES.help.${value}`),
        text: I18n.text(`DateRangePicker.ROLLING_DATE_TYPES.text.${value}`),
        value
      }));
    };
    this.handleChange = (dateRange, useFiscalYear = false) => {
      const {
        onChange,
        isFiscalYearEnabled
      } = this.props;
      const shouldUseFiscalYear = useFiscalYear && isFiscalYearEnabled;
      onChange(dateRange, shouldUseFiscalYear);
    };
    this.handleRangeChange = rangeType => {
      const {
        rollingDays,
        entireCurrentUnit,
        startDate,
        endDate,
        date,
        format,
        fiscalYearMonthName,
        isFiscalYearEnabled
      } = this.props;
      const useFiscalYear = isFiscalYearRangeType(rangeType);
      rangeType = useFiscalYear ? FISCAL_YEAR_TO_STANDARD_DATE_RANGE[rangeType] : rangeType;
      const shouldUseFiscalYear = useFiscalYear && isFiscalYearEnabled;
      const dateRange = makeDateRangeByType(Object.assign({
        rangeType
      }, rollingDays ? {
        rollingDays
      } : {}, {
        entireCurrentUnit,
        startDate,
        endDate,
        date
      }), format, undefined, shouldUseFiscalYear, fiscalYearMonthName);
      this.handleChange(dateRange, shouldUseFiscalYear);
    };
    this.handleRollingDateChange = ({
      currentTarget: {
        value: rollingDays
      }
    }) => {
      const dateRange = makeDateRangeByType(Object.assign({}, this.props, {
        rollingDays
      }), this.props.format);
      this.handleChange(dateRange);
    };
    this.handleCustomDateChange = ({
      target: {
        value
      }
    }) => {
      const {
        startDate,
        endDate
      } = value;
      const {
        format
      } = this.props;
      if (isValid(startDate) && isValid(endDate)) {
        const dateRange = makeDateRangeByType(Object.assign({}, this.props, {
          startDate: toMoment(startDate).format(format),
          endDate: toMoment(endDate).format(format)
        }), format);
        this.handleChange(dateRange);
      }
    };
    this.handleSingleDateChange = ({
      target: {
        value: date
      }
    }) => {
      const {
        format
      } = this.props;
      const dateRange = makeDateRangeByType(Object.assign({}, this.props, {
        date: toMoment(date).format(format)
      }), format);
      this.handleChange(dateRange);
    };
    this.handleEntireUnitToggle = entireCurrentUnit => {
      const {
        useFiscalYear,
        fiscalYearMonthName,
        format,
        isFiscalYearEnabled
      } = this.props;
      const shouldUseFiscalYear = useFiscalYear && isFiscalYearEnabled;
      const dateRange = makeDateRangeByType(Object.assign({}, this.props, {
        entireCurrentUnit
      }), format, undefined, shouldUseFiscalYear, fiscalYearMonthName);
      this.handleChange(dateRange, shouldUseFiscalYear);
    };
    this.renderEntireUnitToggle = () => {
      const {
        rangeType,
        entireCurrentUnit,
        hideEntireCurrentUnit,
        disabled
      } = this.props;
      if (!rangeType || hideEntireCurrentUnit) {
        return null;
      }
      const [timeFrame, dateUnit] = rangeType.split('_');
      if (timeFrame && timeFrame === 'THIS' && dateUnit !== 'DAY') {
        return /*#__PURE__*/_jsx(Checkbox, {
          alignSelf: "baseline",
          "data-test-id": "entire-timeframe-checkbox",
          innerPadding: "none",
          checked: entireCurrentUnit,
          disabled: disabled,
          onChange: this.props.partial(this.handleEntireUnitToggle, !entireCurrentUnit),
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: `DateRangePicker.entireCurrentUnit.${dateUnit}`
          })
        });
      }
      return null;
    };
    this.renderDateRange = () => {
      const {
        rangeType,
        startDate,
        endDate,
        disabled,
        dateRangeInputUse,
        format,
        maxCustomValue,
        minCustomValue,
        outOfBoundsMessage
      } = this.props;
      const start = I18n.moment.isMoment(startDate) ? startDate : I18n.moment(startDate, format);
      const end = I18n.moment.isMoment(endDate) ? endDate : I18n.moment(endDate, format);
      const restrictedMaxCustomValue = maxCustomValue && compare(fromMoment(I18n.moment().add(10, 'year')), maxCustomValue) !== -1 ? maxCustomValue : fromMoment(I18n.moment().add(10, 'year'));
      const restrictedMinCustomValue = minCustomValue && compare(fromMoment(I18n.moment([2006, 0, 1])), minCustomValue) !== 1 ? minCustomValue : fromMoment(I18n.moment([2006, 0, 1]));
      const validationMessage = outOfBoundsMessage ? {
        maxValidationMessage: outOfBoundsMessage,
        minValidationMessage: outOfBoundsMessage
      } : {};
      if (rangeType === RANGE_TYPES.CUSTOM) {
        return /*#__PURE__*/_jsx(FormControl, {
          validationMessage: outOfBoundsMessage,
          error: !!outOfBoundsMessage,
          children: /*#__PURE__*/_jsx(MicroDateRange, Object.assign({
            "data-test-id": "custom-date-range",
            disabled: disabled,
            onChange: this.handleCustomDateChange,
            showPresets: false,
            use: dateRangeInputUse,
            value: {
              startDate: fromMoment(start),
              endDate: fromMoment(end)
            },
            max: restrictedMaxCustomValue,
            min: restrictedMinCustomValue
          }, validationMessage))
        });
      }
      return null;
    };
    this.renderRollingDates = () => {
      const {
        buttonSize,
        buttonUse,
        condensed,
        menuWidth,
        rangeType,
        rollingDays,
        disabled
      } = this.props;
      if (rangeType === RANGE_TYPES.ROLLING) {
        return /*#__PURE__*/_jsx(View, {
          inlineSize: condensed ? '100%' : undefined,
          children: /*#__PURE__*/_jsx(Select, {
            buttonSize: buttonSize,
            buttonUse: buttonUse,
            disabled: disabled,
            menuWidth: menuWidth,
            onChange: this.handleRollingDateChange,
            options: this.getRollingDateRangeSelectOptions(),
            placeholder: I18n.text(`DateRangePicker.ROLLING_DATE_TYPES.text.${rollingDays || 'default'}`),
            searchable: false,
            value: rollingDays,
            "data-test-id": 'dateRangePicker-rollingDates-options'
          })
        });
      }
      return null;
    };
    this.renderFullUnitDates = () => {
      const {
        rangeType,
        date
      } = this.props;
      if (!CUSTOM_UNIT_RANGE_TYPES.includes(rangeType)) {
        return null;
      }
      return /*#__PURE__*/_jsx(FullDatePeriodInput, {
        rangeType: rangeType,
        date: date,
        onChange: range => this.handleChange(makeDateRangeByType(range))
      });
    };
  }
  renderSingleDate() {
    const {
      rangeType,
      date,
      format
    } = this.props;
    const inputDate = I18n.moment.isMoment(date) ? date : I18n.moment(date, format);
    if ([RANGE_TYPES.IS_EQUAL_TO, RANGE_TYPES.IS_BEFORE_DATE, RANGE_TYPES.IS_AFTER_DATE].includes(rangeType)) {
      return /*#__PURE__*/_jsx(DateInput, {
        value: fromMoment(inputDate),
        onChange: this.handleSingleDateChange
      });
    }
    return null;
  }
  render() {
    const {
      rangeType,
      buttonSize,
      buttonUse,
      children,
      condensed,
      placeholder,
      disabled,
      menuWidth,
      useFiscalYear,
      isFiscalYearEnabled
    } = this.props;
    const updatedRangeType = isFiscalYearEnabled && useFiscalYear && STANDARD_TO_FISCAL_YEAR_DATE_RANGE[rangeType] || rangeType;
    const currentOption = updatedRangeType ? I18n.text(`DateRangePicker.RANGE_TYPES.text.${updatedRangeType}`) : placeholder;
    let dropdownButtonInlineSize = undefined;
    if (buttonUse === 'form') {
      dropdownButtonInlineSize = '200px';
    }
    if (condensed) {
      dropdownButtonInlineSize = '100%';
    }
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: condensed ? 'column' : 'row',
      inlineSize: condensed ? '100%' : undefined,
      gap: '200',
      children: [/*#__PURE__*/_jsxs(Dropdown, {
        "data-test-id": 'dateRangePicker-dropdown',
        buttonText: currentOption,
        buttonSize: buttonSize,
        buttonStyles: {
          inlineSize: dropdownButtonInlineSize,
          paddingInline: '300'
        },
        buttonUse: buttonUse,
        disabled: disabled,
        menuWidth: menuWidth,
        children: [/*#__PURE__*/_jsx(View, {
          maxBlockSize: "300px",
          overflowY: "scroll",
          children: /*#__PURE__*/_jsx(List, {
            "data-test-id": 'dateRangePicker-UIList',
            children: this.getDropdownOptions(this.getRangeTypeOptions(), this.handleRangeChange)
          })
        }), children]
      }), this.renderRollingDates(), this.renderFullUnitDates(), this.renderDateRange(), this.renderSingleDate(), this.renderEntireUnitToggle()]
    });
  }
}
DateRangePicker.defaultProps = {
  buttonUse: 'transparent',
  dateRangeInputUse: 'on-dark',
  disabled: false,
  entireCurrentUnit: false,
  format: DATE_FORMAT,
  menuWidth: 'auto',
  rangeType: null,
  rollingDays: ROLLING_DATE_TYPES['7'],
  showRelativeRangeTypes: false,
  useFiscalYear: false,
  isFiscalYearEnabled: false
};
DateRangePicker.propTypes = {
  buttonUse: PropTypes.string,
  children: PropTypes.node,
  condensed: PropTypes.bool,
  date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  dateRangeInputUse: PropTypes.string,
  disabled: PropTypes.bool,
  endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  entireCurrentUnit: PropTypes.bool,
  format: PropTypes.string,
  hideEntireCurrentUnit: PropTypes.bool,
  maxCustomValue: PropTypes.instanceOf(SimpleDate),
  menuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  minCustomValue: PropTypes.instanceOf(SimpleDate),
  onChange: PropTypes.func.isRequired,
  partial: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  rangeType: PropTypes.string,
  rangeTypeOptions: PropTypes.array,
  rangeTypeOverrides: PropTypes.array,
  rollingDateOptions: PropTypes.array,
  rollingDateOverrides: PropTypes.array,
  rollingDays: PropTypes.number,
  showRelativeRangeTypes: PropTypes.bool,
  startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  useFiscalYear: PropTypes.bool,
  gates: PropTypes.array,
  fiscalYearMonthName: PropTypes.string,
  isFiscalYearEnabled: PropTypes.bool,
  outOfBoundsMessage: PropTypes.string,
  buttonSize: PropTypes.string
};
const DateRangePickerWithContextProviders = props => /*#__PURE__*/_jsx(UserInfoContext.Consumer, {
  children: ({
    gates
  }) => /*#__PURE__*/_jsx(FiscalYearContext.Consumer, {
    children: fiscalYearMonthName => /*#__PURE__*/_jsx(DateRangePicker, Object.assign({
      gates: gates,
      fiscalYearMonthName: fiscalYearMonthName
    }, props))
  })
});
export default withPartials(DateRangePickerWithContextProviders);